import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * nativeSpeech.js — locución con el motor del teléfono, no con el del WebView.
 *
 * Adentro del WebView de Android **no existe** `window.speechSynthesis`:
 * verificado en un Xiaomi con la app instalada, donde asumir lo contrario tiró
 * `Cannot read properties of undefined` y se llevó puesta la pantalla de
 * ajustes entera. La app en cambio ya tiene un motor de TTS afinado para esto
 * —`AlertSpeaker`, el que locuta con el teléfono bloqueado— y este módulo es el
 * puente hacia él (`NativeSpeechPlugin`).
 *
 * `lib/speech.js` decide cuál usar, y es de ahí que sale la respuesta para todo
 * lo que dependa de la plataforma (`needsCrossTabCoordination`). La única otra
 * importación legítima es `lib/earcons.js`, que está por DEBAJO de speech.js y
 * no puede preguntarle sin armar un ciclo: necesita saber si el tono lo toca el
 * motor del teléfono para hacerse a un lado. Cualquier otro módulo que pregunte
 * acá está escribiendo la misma decisión de plataforma por tercera vez.
 */
const NativeSpeech = registerPlugin("NativeSpeech");

/**
 * ¿Hay motor nativo en este dispositivo?
 *
 * Sincrónico a propósito: `isSpeechSupported()` lo consulta en pleno render.
 * `isPluginAvailable` mira la lista de plugins registrados por el contenedor,
 * así que en web y en iOS (donde todavía no existe el proyecto nativo) devuelve
 * false y el llamador se queda con la Web Speech API.
 */
export function isNativeSpeechAvailable() {
  try {
    return Capacitor.isPluginAvailable("NativeSpeech");
  } catch {
    return false;
  }
}

/**
 * @param {object} args
 * @param {string} args.text
 * @param {number} args.rate 0.5..2, igual que la Web Speech API. Sin default a
 *   propósito: quien llama es `speech.js`, que ya resuelve el suyo
 *   (`DEFAULT_RATE`). Una copia más del número acá era una copia más para
 *   mantener en línea con la tarjeta y con `VoicePreferences.DEFAULT_RATE`.
 * @param {boolean} [args.urgent] true → suena por el canal de ALARMA, que es el
 *   volumen que la gente deja arriba cuando silencia el teléfono de noche
 * @param {string|null} [args.voiceURI] el `Voice.getName()` de Android. Sin
 *   esto el motor elige la recomendada en español (ver `SpanishVoices.java`).
 * @param {boolean} [args.earcon] tono corto antes de la frase
 * @param {string|null} [args.alertType] de qué alerta se trata, para elegir la
 *   familia del tono
 * @returns {Promise<boolean>} true si el motor aceptó la frase. Nunca rechaza:
 *   un fallo de locución no puede romper la cola de alertas.
 */
export async function nativeSpeak({
  text,
  rate,
  urgent = true,
  voiceURI = null,
  earcon = false,
  alertType = null,
}) {
  try {
    await NativeSpeech.speak({ text, rate, urgent, voiceURI, earcon, alertType });
    return true;
  } catch {
    return false;
  }
}

/** Corta lo que se esté locutando y descarta la cola. */
export async function nativeCancelSpeech() {
  try {
    await NativeSpeech.stop();
  } catch {
    /* no-op */
  }
}

/**
 * Catálogo de voces en español del motor del teléfono.
 *
 * El WebView no lo puede mirar por su cuenta —`speechSynthesis.getVoices()`
 * devuelve vacío ahí dentro—, y hasta que existió este método la tarjeta de
 * ajustes escondía el selector de voz entero en la app: el usuario veía media
 * docena de voces en el navegador y NINGUNA en el teléfono, sin explicación.
 *
 * Viene ya ordenado por el mismo criterio que usa la locución de segundo plano
 * (`SpanishVoices.java`), así que la primera de la lista ES la recomendada.
 *
 * @returns {Promise<Array<{voiceURI: string, name: string, lang: string}>>}
 *   vacío si el motor no está listo o el APK es anterior a este método
 */
export async function nativeListVoices() {
  try {
    const { voices } = await NativeSpeech.getVoices();
    return Array.isArray(voices) ? voices : [];
  } catch {
    return [];
  }
}

/**
 * Deja las preferencias de voz donde la locución de SEGUNDO plano las pueda
 * leer.
 *
 * Con la app cerrada quien habla no es este WebView sino `AlertPresenter`, y
 * ahí no hay `localStorage`: las preferencias tienen que estar copiadas del
 * lado nativo o no surten efecto: hasta que existió esto, "solo el conductor" y
 * "deletrear la placa" no cambiaban nada con el teléfono bloqueado, que es
 * justo el caso que justifica la app.
 *
 * Viaja todo lo que decide si se locuta y cómo: el switch general, la lista de
 * tipos (`Decir esta alerta en voz alta`, en Alertas), la forma de la frase, la
 * voz elegida y si va un tono antes. Los dos últimos empezaron a viajar cuando
 * el tono pasó a tocarlo el motor nativo: mientras lo tocaba el WebView no
 * tenía sentido copiarlo, porque con la app cerrada el WebView no existe.
 *
 * @param {object} prefs
 * @param {boolean} prefs.enabled
 * @param {string} prefs.identify
 * @param {boolean} prefs.phonetic
 * @param {number} prefs.rate
 * @param {boolean} prefs.earcons
 * @param {string|null} prefs.voiceURI
 * @param {Record<string, boolean>} prefs.types
 */
export async function nativeSetVoicePreferences({
  enabled,
  identify,
  phonetic,
  rate,
  earcons,
  voiceURI,
  types,
}) {
  try {
    await NativeSpeech.setPreferences({
      enabled,
      identify,
      phonetic,
      rate,
      earcons,
      voiceURI,
      types,
    });
  } catch {
    // APK anterior a `setPreferences`: el método no existe y el puente rechaza.
    // La locución de segundo plano sigue con el default de siempre (la placa,
    // siempre hablando), que es exactamente lo que hacía antes.
  }
}
